import { useCallback, useEffect, useRef } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  ALargeSmall as IconALargeSmall,
  AlignCenter as IconAlignCenter,
  AlignJustify as IconAlignJustify,
  AlignLeft as IconAlignLeft,
  AlignRight as IconAlignRight,
  Baseline as IconBaseLine,
  Bold as IconBold,
  Eraser as IconEraser,
  Highlighter as IconHighlighter,
  Italic as IconItalic,
  Link2 as IconLink2,
  Link2Off as IconLink2Off,
  List as IconList,
  ListOrdered as IconListOrdered,
  Underline as IconUnderline,
  IndentDecrease,
  IndentIncrease,
  RotateCcw,
  RotateCw,
  SeparatorHorizontal,
} from 'lucide-react';

import EditorButton from './EditorButton';
import FontSize from './FontSize';
import Indent from './Ident';
import OrderedList from './OrderedList';

const sizes = [
  {
    label: '10',
    value: '10pt',
  },
  {
    label: '12',
    value: '12pt',
  },
  {
    label: '14',
    value: '14pt',
  },
  {
    label: '16',
    value: '16pt',
  },
  {
    label: '18',
    value: '18pt',
  },
  {
    label: '20',
    value: '20pt',
  },
  {
    label: '22',
    value: '22pt',
  },
  {
    label: '24',
    value: '24pt',
  },
  {
    label: '26',
    value: '26pt',
  },
  {
    label: '32',
    value: '32pt',
  },
  {
    label: '48',
    value: '48pt',
  },
];

interface IProps {
  content: string;
  onChangeText(html: string): void;
  placeholder?: string;
}

const orderedOptions = [
  { value: 'decimal', label: '1,2,3,5,5' },
  { value: 'lower-alpha', label: 'a,b,c,d,e' },
  { value: 'upper-alpha', label: 'A,B,C,D,E' },
  { value: 'lower-roman', label: 'i,ii,iii,iv,v' },
  { value: 'upper-roman', label: 'I,II,II,IV,V' },
];

function Editor({ content, onChangeText, placeholder }: IProps) {
  const colorRef = useRef<HTMLInputElement>(null);
  const highlighterRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    onUpdate: (html) => {
      onChangeText(html.editor.getHTML());
    },
    extensions: [
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https'],
        isAllowedUri: (url, ctx) => {
          try {
            const parsedUrl = url.includes(':')
              ? new URL(url)
              : new URL(`${ctx.defaultProtocol}://${url}`);

            if (!ctx.defaultValidate(parsedUrl.href)) {
              return false;
            }

            const disallowedProtocols = ['ftp', 'file', 'mailto'];
            const protocol = parsedUrl.protocol.replace(':', '');

            if (disallowedProtocols.includes(protocol)) {
              return false;
            }

            const allowedProtocols = ctx.protocols.map((p) =>
              typeof p === 'string' ? p : p.scheme,
            );

            if (!allowedProtocols.includes(protocol)) {
              return false;
            }

            const disallowedDomains = [
              'example-phishing.com',
              'malicious-site.net',
            ];
            const domain = parsedUrl.hostname;

            if (disallowedDomains.includes(domain)) {
              return false;
            }

            return true;
          } catch {
            return false;
          }
        },
        shouldAutoLink: (url) => {
          try {
            const parsedUrl = url.includes(':')
              ? new URL(url)
              : new URL(`https://${url}`);

            const disallowedDomains = [
              'example-no-autolink.com',
              'another-no-autolink.com',
            ];
            const domain = parsedUrl.hostname;

            return !disallowedDomains.includes(domain);
          } catch {
            return false;
          }
        },
      }),
      Underline,
      OrderedList,
      Placeholder.configure({
        placeholder,
      }),
      StarterKit.configure({
        orderedList: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle.configure({ mergeNestedSpanStyles: true }),
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      FontSize,
      Indent,
    ],
    content,
    editorProps: {
      attributes: {
        class:
          'outline-none min-h-96 p-4 font-light [&>ul]:[&>li]:list-disc [&>ul]:[&>li]:[&>ul]:list-[circle] [&>ul]:[&>li]:[&>ul]:[&>li]:[&>ul]:list-[square]',
      },
    },
  });

  const setLink = useCallback(() => {
    try {
      if (editor) {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);

        if (url === null) {
          return;
        }

        if (url === '') {
          editor.chain().focus().extendMarkRange('link').unsetLink().run();

          return;
        }

        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: url })
          .run();
      }
    } catch (e: any) {
      alert(e.message);
    }
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    for (const extension of editor.extensionManager.extensions) {
      if (extension.name === 'placeholder') {
        extension.options.placeholder = placeholder;
      }
    }

    editor.setOptions();
  }, [editor, placeholder]);

  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-col rounded-md border outline-none">
      <div className="bg-background z-9 sticky top-0 mx-2 flex flex-row flex-wrap gap-2 border-b py-1">
        <EditorButton
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
          title="Desfazer">
          <RotateCcw />
        </EditorButton>
        <EditorButton
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
          title="Refazer">
          <RotateCw />
        </EditorButton>
        <div className="bg-border w-px" />
        <EditorButton
          data-active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Negrito">
          <IconBold />
        </EditorButton>
        <EditorButton
          data-active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Sublinhado">
          <IconUnderline />
        </EditorButton>
        <EditorButton
          data-active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Itálico">
          <IconItalic />
        </EditorButton>
        <div className="bg-border w-px" />
        <EditorButton
          data-active={editor.isActive({ textAlign: 'justify' })}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          title="Justificado">
          <IconAlignJustify />
        </EditorButton>

        <EditorButton
          data-active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title="À esquerda">
          <IconAlignLeft />
        </EditorButton>

        <EditorButton
          data-active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title="Centralizado">
          <IconAlignCenter />
        </EditorButton>

        <EditorButton
          data-active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title="À direita">
          <IconAlignRight />
        </EditorButton>
        <div className="bg-border w-px" />
        <EditorButton
          data-active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="À direita">
          <IconList />
        </EditorButton>

        <Popover>
          <PopoverTrigger asChild>
            <EditorButton title="Lista ordenada">
              <IconListOrdered />
            </EditorButton>
          </PopoverTrigger>
          <PopoverContent className="flex w-32 flex-col p-2">
            {orderedOptions.map((item) => (
              <button
                className="hover:bg-tertiary hover:text-background font-base rounded-sm p-1 text-sm"
                key={item.value}
                onClick={() => {
                  if (!editor.isActive('orderedList')) {
                    editor.chain().focus().toggleOrderedList().run();
                    editor.commands.updateAttributes('orderedList', {
                      listStyleType: item.value,
                    });
                  }
                  editor.commands.updateAttributes('orderedList', {
                    listStyleType: item.value,
                  });
                }}>
                {item.label}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        <div className="bg-border w-px" />
        <EditorButton
          onClick={() => editor.chain().focus().indent().run()}
          title="Indentar">
          <IndentIncrease />
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().outdent().run()}
          title="Desindentar">
          <IndentDecrease />
        </EditorButton>
        <div className="bg-border w-px" />
        <Popover>
          <PopoverTrigger asChild>
            <EditorButton title="Tamanho da fonte">
              <IconALargeSmall />
            </EditorButton>
          </PopoverTrigger>
          <PopoverContent className="flex w-11 flex-col p-2">
            {sizes.map((item) => (
              <button
                className="hover:bg-primary hover:text-background rounded-sm p-1 text-sm font-light"
                key={item.value}
                onClick={() =>
                  editor.chain().focus().setFontSize(item.value).run()
                }>
                {item.label}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        <div className="relative">
          <EditorButton
            onClick={() => colorRef.current?.click()}
            title="Cor do texto">
            <IconBaseLine />
            <input
              className="absolute cursor-pointer opacity-0"
              onChange={(event) =>
                editor.chain().focus().setColor(event.target.value).run()
              }
              ref={colorRef}
              type="color"
            />
          </EditorButton>
        </div>
        <div className="relative">
          <EditorButton
            onClick={() => highlighterRef.current?.click()}
            title="Cor do fundo">
            <IconHighlighter />
            <input
              className="absolute cursor-pointer opacity-0"
              onChange={(event) =>
                editor
                  .chain()
                  .focus()
                  .toggleHighlight({ color: event.target.value })
                  .run()
              }
              ref={highlighterRef}
              type="color"
            />
          </EditorButton>
        </div>
        <EditorButton
          onClick={() =>
            editor.chain().focus().clearNodes().unsetAllMarks().run()
          }
          title="Limpar formatação">
          <IconEraser />
        </EditorButton>
        <div className="bg-border w-px" />
        <EditorButton
          data-active={editor.isActive('link')}
          onClick={setLink}
          title="Inserir link">
          <IconLink2 />
        </EditorButton>
        <EditorButton
          disabled={!editor.isActive('link')}
          onClick={() => editor.chain().focus().unsetLink().run()}
          title="Remover link">
          <IconLink2Off />
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Inserir linha horizontal">
          <SeparatorHorizontal />
        </EditorButton>
      </div>
      <EditorContent
        className="prose text-foreground prose-p:mb-1.5 prose-strong:text-inherit prose-p:mt-0 prose-li:my-0 prose-ul:ml-6 prose-ul:mt-0 prose-ul:mb-5 marker:text-foreground max-w-screen-xl"
        editor={editor}
      />
    </div>
  );
}

export default Editor;
